import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import { useAlert } from '@weave/alert-system';
import { Page } from '../../shared';
import { useWiePayerMixQuery } from 'apis/insurance/queries';
import { PayerMixSummary } from './components/PayerMixSummary';
import { PayerMixDetailsTable } from './components/PayerMixDetailsTable';
import { selectCurrentLocationId } from '../../../redux/actions/location';
import { RecommendedConnections } from './components/RecommendedConnections';
import { PmsCapabilitiesChips } from './components/PmsCapabilitiesChips';
import { Store } from '../../../redux/store/store.model';
import { selectCustomizationFeature } from '../../../redux/actions/customization/customization.action';
import { CustomizationFlagStatesV2 } from 'models/location-feature.model';
import { FeatureEnumToNameMap } from 'constants/feature-data';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import {
  selectSidebarSyncApps,
  selectLoadingSyncApps,
} from 'redux/actions/sync-app/sync-app.selectors';
import { getSyncAppHealth } from 'redux/actions/sync-app/sync-app.actions';

const EXCLUDED_PMS = ['CSV', 'CUSTOM_CONTACT'];

export const InsurancePayerMix = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const locationId = useSelector(selectCurrentLocationId);
  const { query: wiePayerMixQuery } = useWiePayerMixQuery({
    locationId: locationId ?? '',
  });
  const isEnhancedEligibilityCustomizationEnabled =
    useSelector((state: Store) =>
      selectCustomizationFeature(
        state,
        FeatureEnumToNameMap[Feature.ENHANCED_INSURANCE_VERIFICATION] ?? ''
      )
    )?.state === CustomizationFlagStatesV2.ACTIVE;

  // Get sync apps from the redux store to determine the location's PMS
  const namedSyncApps = orderBy(useSelector(selectSidebarSyncApps), ['SourceName']);
  const isSyncAppsLoading = useSelector(selectLoadingSyncApps);

  // Fetch sync app health when opening a child location directly,
  // so that PMS name is available even if the sidebar hasn't loaded it yet.
  useEffect(() => {
    if (locationId && namedSyncApps.length === 0 && !isSyncAppsLoading) {
      dispatch(getSyncAppHealth());
    }
  }, [locationId, namedSyncApps.length]);

  // Resolve the PMS name from the first non-excluded sync app
  const pmsName = useMemo(() => {
    const pmsSyncApp = namedSyncApps.find(
      (app) => !EXCLUDED_PMS.includes(app.PracticeManagementSystem)
    );
    return pmsSyncApp?.PracticeManagementSystem || undefined;
  }, [namedSyncApps]);

  useEffect(() => {
    if (wiePayerMixQuery.isError) {
      alert.error('Failed to fetch payer mix data. Please try again later.');
    }
  }, [wiePayerMixQuery.isError]);

  if (!locationId || wiePayerMixQuery.isError) {
    return null;
  }

  return (
    <Page title="Insurance Payer Mix" loading={wiePayerMixQuery.isLoading}>
      <PayerMixSummary
        summary={wiePayerMixQuery?.data?.summary}
        isEnhancedEligibilityCustomizationEnabled={
          isEnhancedEligibilityCustomizationEnabled
        }
      />
      <PmsCapabilitiesChips pmsName={pmsName} isLoading={isSyncAppsLoading} />
      <RecommendedConnections
        connections={wiePayerMixQuery?.data?.recommendedConnections}
      />
      <PayerMixDetailsTable payerMixDetails={wiePayerMixQuery?.data?.payerMixDetails} />
    </Page>
  );
};
